import "./App.css";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import LoginPage from "./pages/login/Login";
import MiddlewareAuth from "./components/middleware/MiddlewareAuth";
import Loading from "./components/main/Loading";
import Toast from "./components/main/Toast";
import Home from "./pages/home/Home";
import { ToastContainer } from "react-toastify";
import ForgotPassword from "./pages/login/ForgotPassw";
import AsignPassword from "./pages/login/AsignPassword";
import Profile from "./pages/account/Profile";
import EditProfile from "./pages/account/EditProfile";
import LoadsComponent from "./pages/loads/LoadsComponent";
import LoadDetail from "./pages/loads/LoadDetail";
import StopDetails from "./pages/stops/StopDetails";
import ServiceRequest from "./pages/ServiceRequest/ServiceRequest";
import ServiceRequestNew from "./pages/ServiceRequest/ServiceRequestNew";
import { useEffect, useRef, useState } from "react";
import { getAuthUser, getCurrentAuthUser, isUserAuthenticated, respondOperatorLocation } from "./helpers/configRoutes/backend_helper";
import { disconnectEcho, getEchoInstance } from "./helpers/realtime/echo";

const App = () => {
  const [sessionUser, setSessionUser] = useState(() => getAuthUser());
  const echoRef = useRef(null);
  const listenerStatusRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const syncAuthUser = async () => {
      const authUserRaw = localStorage.getItem("authUser");

      if (!authUserRaw) {
        if (!cancelled) setSessionUser(null);
        return;
      }

      const authUser = JSON.parse(authUserRaw);

      if (authUser?.user?.id) {
        if (!cancelled) setSessionUser(authUser.user);
        return;
      }

      if (!authUser?.token) {
        if (!cancelled) setSessionUser(null);
        return;
      }

      try {
        const currentUser = await getCurrentAuthUser();

        if (currentUser?.id) {
          const hydratedAuthUser = {
            ...authUser,
            user: currentUser,
            isAuth: true,
          };

          localStorage.setItem("authUser", JSON.stringify(hydratedAuthUser));

          if (!cancelled) {
            console.info('[Operator location] Auth user hydrated from /user', currentUser.id);
            setSessionUser(currentUser);
          }
        } else if (!cancelled) {
          console.warn('[Operator location] /user did not return a valid user id', currentUser);
          setSessionUser(null);
        }
      } catch (error) {
        console.warn('[Operator location] Unable to hydrate auth user', error);
      }
    };

    syncAuthUser();
    const intervalId = window.setInterval(syncAuthUser, 3000);
    window.addEventListener("storage", syncAuthUser);
    window.addEventListener("authUserChanged", syncAuthUser);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener("storage", syncAuthUser);
      window.removeEventListener("authUserChanged", syncAuthUser);
    };
  }, []);

  useEffect(() => {
    const listenerStatus = JSON.stringify({
      authenticated: isUserAuthenticated(),
      userId: sessionUser?.id || null,
      alreadyListening: Boolean(echoRef.current),
    });

    if (listenerStatusRef.current !== listenerStatus) {
      listenerStatusRef.current = listenerStatus;
      console.info('[Operator location] Listener status', JSON.parse(listenerStatus));
    }

    if (!isUserAuthenticated() || !sessionUser?.id || echoRef.current) {
      return undefined;
    }

    echoRef.current = getEchoInstance();
    const channelName = `operator-location-requests.${sessionUser.id}`;
    console.info(`[Operator location] Listening on ${channelName}`);

    const channel = echoRef.current.private(channelName);

    if (typeof channel.error === "function") {
      channel.error((error) => {
        console.warn('[Operator location] Private channel subscription error', error);
      });
    }

    if (typeof channel.subscribed === "function") {
      channel.subscribed(() => {
        console.info(`[Operator location] Subscribed to ${channelName}`);
      });
    }

    channel.listen('.OperatorLocationRequested', (data) => {
      console.info('[Operator location] Request received', data);

      if (!navigator.geolocation) {
        console.warn('[Operator location] Geolocation is not supported by this browser');
        return;
      }

      console.info('[Operator location] Requesting browser location', {
        isSecureContext: window.isSecureContext,
        origin: window.location.origin,
      });

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude, accuracy, heading, speed } = position.coords;
          console.info('[Operator location] Browser location resolved', { latitude, longitude, accuracy });

          try {
            const response = await respondOperatorLocation({
              request_id: data.request_id,
              map_session_id: data.map_session_id,
              requester_id: data.requester_id,
              company_id: data.company_id,
              load_ids: data.load_ids || [],
              lat: latitude,
              lng: longitude,
              accuracy,
              heading,
              speed,
            });

            console.info('[Operator location] Location response sent', response);
          } catch (error) {
            console.warn('[Operator location] Location response failed', error);
          }
        },
        (error) => {
          console.warn('[Operator location] Unable to share operator location', error);
        },
        {
          enableHighAccuracy: true,
          maximumAge: 4000,
          timeout: 8000,
        }
      );
    });

    return () => {
      echoRef.current?.leave(channelName);
      disconnectEcho();
      echoRef.current = null;
    };
  }, [sessionUser?.id]);

  return (
    <>
      <Toast />
      <ToastContainer />
      <Loading /> 
      <Router>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<AsignPassword />} />
          <Route path="/confirm-account/:token" element={<AsignPassword />} />

          <Route
            path="/"
            element={ 
              <MiddlewareAuth>
                <Home />
              </MiddlewareAuth>
            }
          />
          <Route
            path="/profile"
            element={
              <MiddlewareAuth>
                <Profile />
              </MiddlewareAuth>
            }
          />

          <Route
            path="/edit-profile"
            element={
              <MiddlewareAuth>
                <EditProfile />
              </MiddlewareAuth>
            }
          />
          <Route
            path="/loads"
            element={
              <MiddlewareAuth>
                <LoadsComponent />
              </MiddlewareAuth>
            }
          />
          <Route
            path="/active-load/:id"
            element={
              <MiddlewareAuth>
                <LoadDetail />
              </MiddlewareAuth>
            }
          />
          <Route
            path="/active-stop/:load_id/:id"
            element={
              <MiddlewareAuth>
                <StopDetails />
              </MiddlewareAuth>
            }
          />
          <Route
            path="/service-request/"
            element={
              <MiddlewareAuth>
                <ServiceRequest />
              </MiddlewareAuth>
            }
          />

          <Route
            path="/request/new"
            element={
              <MiddlewareAuth>
                <ServiceRequestNew />
              </MiddlewareAuth>
            }
          />
          


        </Routes>
      </Router>
    </>
  );
};

export default App;
